import cn from "classnames"

import { Box, Text } from "ui-kit/components-branded/index"
import { Title } from "modules/HRAccount/Title"
import { HelpTooltip } from "components"
import { MonthReport } from "modules/HRAccount/api"
import { CURRENCY } from "utils/constants"
import style from "modules/HRAccount/StatsTable/statsTable.module.scss"
import { allMonths, capitalize, formatPrice } from "utils/utils"

const months = allMonths()

function getDiff(current: number, prev: number) {
    if (current === prev) return
    const percent = Math.abs(Math.round(((current - prev) / prev) * 100))
    if (!Number.isFinite(percent)) return
    return {
        percent,
        isPositive: current - prev > 0,
    }
}

function DiffIndicator({ diff }: { diff: ReturnType<typeof getDiff> }) {
    return diff ? (
        <Text
            isSecondary
            className={cn(
                style.diff,
                diff?.isPositive ? "has-text-alter-light" : "has-text-danger-lite"
            )}
        >
            {diff?.isPositive ? "▴" : "▾"}
            {diff?.percent}%
        </Text>
    ) : null
}

function getMonthName(year: number, month: number) {
    const currentYear = new Date().getFullYear()
    return `${capitalize(months[month - 1])} ${currentYear === year ? "" : year}`
}

function isCurrentMonth(year: number, month: number) {
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() + 1
    return year === currentYear && month === currentMonth
}

export function StatsTable({ statisticByMonth }: { statisticByMonth: MonthReport[] }) {
    const active_employees = statisticByMonth.map((report) => report.active_clients)
    const new_employees = statisticByMonth.map((report) => report.new_clients)
    const continuing_employees = statisticByMonth.map((report) => report.continue_therapy)
    const session_count = statisticByMonth.map((report) => report.session_count)
    const expenses_sum = statisticByMonth.map((report) => report.expenses_sum)
    return (
        <Box className={cn("has-constant-gaps is-big-gaps", style.container)}>
            <Title dataTestId="monthly-statistics" className="mgb-2">
                Показатели по&nbsp;месяцам
            </Title>
            <div className={style.scroller}>
                <table>
                    <thead>
                        <tr className={style.firstRow}>
                            <td className={style.cell} key="title">
                                <Text isAccent className="has-text-dark-60">
                                    Показатель
                                </Text>
                            </td>
                            {statisticByMonth.map((report) => {
                                const name = getMonthName(
                                    report.session_start_datetime__year,
                                    report.session_start_datetime__month
                                )
                                return (
                                    <td className={style.cell} key={name}>
                                        <Text isAccent className="has-text-dark-60">
                                            {capitalize(name)}
                                        </Text>
                                    </td>
                                )
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        <tr className={style.row} key="active_employees">
                            <td className={style.cell}>
                                <Text isAccent>Активные пользователи</Text>
                            </td>
                            {active_employees.map((v, i, list) => (
                                <td key={`active_employees-${v}-${i}`} className={style.cell}>
                                    <Text isAccent>{v}</Text>{" "}
                                    {i > 0 && <DiffIndicator diff={getDiff(v, list[i + 1])} />}
                                </td>
                            ))}
                        </tr>
                        <tr className={style.row} key="new_employees">
                            <td className={style.cell}>
                                <Text isAccent>Новые пользователи</Text>
                            </td>
                            {new_employees.map((v, i, list) => (
                                <td key={`new_employees-${v}-${i}`} className={style.cell}>
                                    <Text isAccent>{v}</Text>{" "}
                                    {i > 0 && <DiffIndicator diff={getDiff(v, list[i + 1])} />}
                                </td>
                            ))}
                        </tr>
                        <tr className={style.row} key="continuing_employees">
                            <td className={style.cell}>
                                <Text isAccent>Продолжают терапию</Text>
                            </td>
                            {continuing_employees.map((v, i, list) => (
                                <td key={`continuing_employees-${v}-${i}`} className={style.cell}>
                                    <Text isAccent>{v}</Text>{" "}
                                    {i > 0 && <DiffIndicator diff={getDiff(v, list[i + 1])} />}
                                </td>
                            ))}
                        </tr>
                        <tr className={style.row} key="session_count">
                            <td className={style.cell}>
                                <Text isAccent>Сессии</Text>
                            </td>
                            {session_count.map((v, i, list) => {
                                const current = statisticByMonth[i]
                                return (
                                    <td key={`session_count-${v}-${i}`} className={style.cell}>
                                        <div className="is-flex">
                                            <Text isAccent>{v}</Text>&nbsp;
                                            {i > 0 && (
                                                <DiffIndicator diff={getDiff(v, list[i + 1])} />
                                            )}
                                            {isCurrentMonth(
                                                current.session_start_datetime__year,
                                                current.session_start_datetime__month
                                            ) && (
                                                <HelpTooltip
                                                    overlay={
                                                        <>Состоявшиеся и&nbsp;запланированные</>
                                                    }
                                                    placement="topLeft"
                                                />
                                            )}
                                        </div>
                                    </td>
                                )
                            })}
                        </tr>
                        <tr className={style.row} key="expenses_sum">
                            <td className={style.cell}>
                                <Text isAccent>Расходы</Text>
                            </td>
                            {expenses_sum.map((v, i, list) => {
                                return (
                                    <td key={`expenses_sum-${v}-${i}`} className={style.cell}>
                                        <Text isAccent>{formatPrice(v, CURRENCY.RUB)}</Text>{" "}
                                        {i > 0 && <DiffIndicator diff={getDiff(v, list[i + 1])} />}
                                    </td>
                                )
                            })}
                        </tr>
                        <tr>
                            <td className={style.fakeCell}></td>
                            {Array.from({ length: statisticByMonth.length }, (_, i) => (
                                <td key={`fake-${i}`} className={style.fakeCell} />
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>
        </Box>
    )
}
